<template>
  <div>
    <transition name="slide-fade">
      <div v-if="visible" class="ship-info-panel">
        <el-card shadow="hover" class="ship-card">
          <div slot="header" class="ship-card-header">
            <span>{{ ship.cnName || ship.shipname || ship.userid }}</span>
            <div class="ship-card-actions">
              <span style="font-size: 10px;padding-top: 5px;">AIS更新时间：{{ aisUpdateTime }}</span>
              <i class="el-icon-edit" @click="onEdit" title="编辑"></i>
              <i class="el-icon-close" @click="onClose" title="关闭"></i>
            </div>
          </div>
          <div v-if="ship" class="ship-info-content">
            <div class="ship-details-grid">
              <div class="ship-details-col">
                <div class="ship-detail-item">
                  <span class="detail-label">MMSI:</span>
                  <span class="detail-value">{{ ship.userid || '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">航速:</span>
                  <span class="detail-value">{{ ship.sog !== undefined ? formatSog(ship.sog) + ' 节' : '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">航向:</span>
                  <span class="detail-value">{{ ship.cog !== undefined ? formatCog(ship.cog) + '°' : '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">艏向:</span>
                  <span class="detail-value">{{ ship.heading !== undefined ? formatCog(ship.heading) + '°' : '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">呼号:</span>
                  <span class="detail-value">{{ ship.callSign || '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">船籍:</span>
                  <span class="detail-value">{{ ship.flag || '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">船型:</span>
                  <span class="detail-value">{{ getVesselTypeName(ship.shipType) || '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">联系人:</span>
                  <span class="detail-value">{{ ship.contacts || '-' }}</span>
                </div>
              </div>
              <div class="ship-details-col">
                <div class="ship-detail-item">
                  <span class="detail-label">经度:</span>
                  <span class="detail-value">{{ ship.pos ? formatLongitudeDMS(ship.pos.longitude) : '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">纬度:</span>
                  <span class="detail-value">{{ ship.pos ? formatLatitudeDMS(ship.pos.latitude) : '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">长度:</span>
                  <span class="detail-value">{{ ship.length ? ship.length + '米' : '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">宽度:</span>
                  <span class="detail-value">{{ ship.width ? ship.width + '米' : '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">吃水:</span>
                  <span class="detail-value">{{ ship.depth ? ship.depth + '米' : '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">载重量:</span>
                  <span class="detail-value">{{ ship.load ? ship.load + '吨' : '-' }}</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">状态:</span>
                  <span class="detail-value">-</span>
                </div>
                <div class="ship-detail-item">
                  <span class="detail-label">联系电话:</span>
                  <span class="detail-value">{{ ship.contactNumber || '-' }}</span>
                </div>
              </div>
            </div>
            
            <div class="ship-detail-remark">
              <div class="ship-detail-item full-width">
                <span class="detail-label">备注:</span>
                <span class="detail-value">{{ ship.remark || '-' }}</span>
              </div>
            </div>
            <div class="panel-footer">
              <el-button type="primary" size="small" icon="el-icon-location" @click="onLocate">定位</el-button>
              <el-button type="success" size="small" icon="el-icon-map-location" @click="onShowTrack">查看航迹</el-button>
              <el-button type="warning" size="small" icon="el-icon-warning" @click="onShowWarning">告警</el-button>
            </div>
          </div>
        </el-card>
      </div>
    </transition>
    
    <!-- 船舶编辑抽屉组件 -->
    <vessel-edit-drawer 
      :visible.sync="editDrawerVisible"
      :vessel="ship"
      @close="handleEditDrawerClose"
      @saved="handleVesselSaved">
    </vessel-edit-drawer>
    
    <!-- 手动告警对话框 -->
    <el-dialog
      title="发送船舶告警"
      :visible.sync="warningDialogVisible"
      top="1.5vh"
      width="800px"
      @closed="resetWarningForm">
      <div class="warning-dialog-content">
        <!-- 告警模板按钮组 -->
        <div class="template-section">
          <div class="template-buttons">
            <el-button
              v-for="item in alertTemplates"
              :key="item.value"
              :type="selectedTemplate === item.value ? 'primary' : 'default'"
              :class="[
                'template-btn',
                selectedTemplate === item.value ? 'selected' : '',
                getTemplateButtonClass(item.value)
              ]"
              size="small"
              @click="handleTemplateSelect(item.value)">
              <i :class="getTemplateIcon(item.value)" class="mr-1"></i>
              {{ item.label }}
            </el-button>
          </div>
        </div>

        <!-- 船舶参数选择（仅当桥梁有告警设备时显示） -->
        <div v-if="bridgeAlertDeviceStatus && bridgeAlertDeviceStatus.hasAlertDevices" class="ship-params-section">
          <h4 class="section-title">
            <i class="el-icon-ship text-indigo-500 mr-2"></i>
            船舶状态
          </h4>
          <div class="params-grid two-column-grid">
            <!-- 船舶行驶方向 -->
            <div class="param-item">
              <label class="param-label">
                <i class="el-icon-sort mr-1"></i>
                船舶行驶方向
              </label>
              <el-radio-group 
                v-model="warningForm.shipDirection" 
                class="param-radio-group">
                <el-radio
                  v-for="item in shipDirectionOptions"
                  :key="item.value"
                  :label="item.value"
                  class="param-radio">
                  {{ item.label }}
                </el-radio>
              </el-radio-group>
            </div>
            
            <!-- 船舶相对位置 -->
            <div class="param-item">
              <label class="param-label">
                <i class="el-icon-location mr-1"></i>
                相对桥梁位置
              </label>
              <el-radio-group 
                v-model="warningForm.distanceTrend" 
                class="param-radio-group">
                <el-radio
                  v-for="item in distanceTrendOptions"
                  :key="item.value"
                  :label="item.value"
                  class="param-radio">
                  {{ item.label }}
                </el-radio>
              </el-radio-group>
            </div>
          </div>
        </div>

        <!-- 告警内容 -->
        <div class="content-section">
          <h4 class="section-title">
            <i class="el-icon-edit-outline text-blue-500 mr-2"></i>
            告警内容
          </h4>
          <el-input 
            type="textarea" 
            v-model="warningForm.description" 
            :rows="2"
            placeholder="请输入告警内容，或选择上方模板自动填充"
            class="content-textarea">
          </el-input>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="warningDialogVisible = false" size="medium">
          <i class="el-icon-close mr-1"></i>取消
        </el-button>
        <el-button type="primary" @click="sendWarning" size="medium" :disabled="!warningForm.description.trim()">
          <i class="el-icon-s-promotion mr-1"></i>发送告警
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { convertToDMS } from '@/utils/mapControls'
import { formatCog as formatCogUtil, formatSog as formatSogUtil } from '@/utils/mapControls'
import BridgeEventBus from '@/utils/BridgeEventBus'
import { alertDataReceiver } from '@/utils/AlertDataReceiver'
import VesselEditDrawer from './VesselEditDrawer.vue'
import { getAllVesselTypes } from '@/api/vessel'
import { sendManualAlert } from '@/api/alert'
import { getBridgeAlertDevices } from '@/api/alert-device'
import { getUTC8ISOString } from '@/utils/dateUtils'

export default {
  name: 'ShipInfoPanel',
  components: {
    VesselEditDrawer
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    ship: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      editDrawerVisible: false,
      warningDialogVisible: false,
      selectedTemplate: '',
      warningForm: {
        description: '',
        alertType: 'YAW',
        alertLevel: 2,
        shipDirection: 1, // 船舶行驶方向：0-下行，1-上行，null-自动判断
        distanceTrend: 0  // 船舶相对位置：0-靠近，1-远离，null-自动判断
      },
      // 桥梁告警设备状态
      bridgeAlertDeviceStatus: null,
      // 船舶行驶方向选项
      shipDirectionOptions: [
        { label: '上行', value: 1 },
        { label: '下行', value: 0 },
        { label: '未知', value: null }
      ],
      // 船舶相对位置选项
      distanceTrendOptions: [
        { label: '靠近桥梁', value: 0 },
        { label: '远离桥梁', value: 1 },
        { label: '未知', value: null }
      ],
      alertTemplates: [
        { label: '偏航警告', value: 'YAW', text: '船舶：{船舶名}，{船舶名}，在{桥梁名} {区域名}偏航，请注意通航安全' },
        { label: '超速警告', value: 'SPEED', text: '船舶：{船舶名}，{船舶名}，在{桥梁名} {区域名}超速，请注意通航安全' },
        { label: '超高警告', value: 'HIGH', text: '船舶：{船舶名}，{船舶名}，在{桥梁名} {区域名}超高，请注意通航安全' },
        { label: '滞留警告', value: 'STAY', text: '船舶：{船舶名}，{船舶名}，在{桥梁名} {区域名}滞留超时，请及时离开' },
        { label: '区域进入警告', value: 'ENTRY', text: '船舶：{船舶名}，{船舶名}，进入{桥梁名} {区域名}，请注意通航安全' },
        { label: '区域离开警告', value: 'EXIT', text: '船舶：{船舶名}，{船舶名}，离开{桥梁名} {区域名}，请注意通航安全' },
        { label: '穿越警告', value: 'CROSS', text: '船舶：{船舶名}，{船舶名}，穿越{桥梁名} {区域名}，请注意通航安全' },
        { label: '黑名单', value: 'BLACKLIST', text: '发现黑名单船舶：{船舶名}，{船舶名}，请立即关注！' },
        { label: '碰撞风险', value: 'COLLISION', text: '船舶：{船舶名}，{船舶名}，在{桥梁名} {区域名}存在碰撞风险，请立即处理！' }
      ],
      vesselTypeOptions: [] // 存储船舶类型列表
    }
  },
  created() {
    // 组件创建时获取船舶类型列表
    this.fetchVesselTypes()
  },
  watch: {
    visible(val) {
      // 当面板显示时确保船舶类型列表已加载
      if (val && this.vesselTypeOptions.length === 0) {
        this.fetchVesselTypes()
      }
    },
  },
  computed: {
    // 格式化AIS更新时间
    aisUpdateTime() {
      if (!this.ship || !this.ship.receive_time) {
        return '--'
      }
      
      try {
        const date = new Date(this.ship.receive_time)
        if (isNaN(date.getTime())) {
          return '--'
        }
        
        // 格式化为 HH:MM:SS 格式
        return date.toLocaleTimeString('zh-CN', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        })
      } catch (e) {
        console.warn('格式化AIS更新时间失败:', e)
        return '--'
      }
    }
  },
  methods: {
    // 获取船舶类型列表
    fetchVesselTypes() {
      getAllVesselTypes().then(response => {
        if (response.code === 200) {
          this.vesselTypeOptions = response.data || []
        }
      }).catch(error => {
        console.error('获取船舶类型列表失败', error)
      })
    },
    
    // 获取船舶类型名称
    getVesselTypeName(typeCode) {
      if (!typeCode) return '-'
      
      const typeInfo = this.vesselTypeOptions.find(item => item.typeCode === typeCode)
      return typeInfo ? typeInfo.typeName : typeCode
    },
    
    // 查询指定桥梁的告警设备状态
    fetchBridgeAlertDevices() {
      // 从localstorage获取当前桥梁ID
      const selectedBridge = JSON.parse(localStorage.getItem('selectedBridge'))
      if (!selectedBridge || !selectedBridge.id) {
        console.warn('未找到选中的桥梁信息')
        return
      }
      
      const currentBridgeId = Number(selectedBridge.id)
      console.log(`查询桥梁 ${currentBridgeId} 的告警设备状态`)
      
      getBridgeAlertDevices(currentBridgeId).then(response => {
        if (response.code === 200) {
          this.bridgeAlertDeviceStatus = response.data
          console.log('桥梁告警设备状态:', this.bridgeAlertDeviceStatus)
        } else {
          console.warn('获取桥梁告警设备状态失败:', response.message)
          this.bridgeAlertDeviceStatus = null
        }
      }).catch(error => {
        console.error('查询桥梁告警设备状态失败', error)
        this.bridgeAlertDeviceStatus = null
      })
    },
    
    // 事件处理方法
    onClose() {
      this.$emit('close')
    },
    onEdit() {
      console.warn('【调试】onEdit',this.ship)
      this.editDrawerVisible = true
    },
    handleEditDrawerClose() {
      this.editDrawerVisible = false
    },
    handleVesselSaved(updatedVessel) {
      // 更新船舶信息
      this.$emit('vessel-updated', updatedVessel)
    },
    onLocate() {
      this.$emit('locate', this.ship.userid)
    },
    onShowTrack() {
      this.$emit('show-track')
    },
    onShowWarning() {
      this.warningDialogVisible = true
      // 弹窗时查询指定桥梁的告警设备状态
      this.fetchBridgeAlertDevices()
    },
    
    // 格式化方法
    formatCog(cog) {
      return formatCogUtil(cog)
    },
    formatSog(sog) {
      return formatSogUtil(sog)
    },
    formatLongitudeDMS(longitude) {
      if (longitude === undefined || longitude === null) return '-'
      return convertToDMS(longitude, 'lng')
    },
    formatLatitudeDMS(latitude) {
      if (latitude === undefined || latitude === null) return '-'
      return convertToDMS(latitude, 'lat')
    },
    
    // 获取船舶显示名称
    shipDisplayName() {
      return this.ship.cnName || this.ship.shipname || this.ship.userid || '未知船舶'
    },
    
    // 处理模板选择（按钮点击）
    handleTemplateSelect(templateValue) {
      this.selectedTemplate = templateValue
      this.handleTemplateChange()
    },
    
    // 处理模板选择
    handleTemplateChange() {
      // 查找选择的模板
      const template = this.alertTemplates.find(t => t.value === this.selectedTemplate)
      if (template) {
        const shipName = this.shipDisplayName()
        // 获取桥梁名称
        let bridgeName = ''
        try {
          const selectedBridge = JSON.parse(localStorage.getItem('selectedBridge'))
          bridgeName = (selectedBridge && selectedBridge.name) ? selectedBridge.name : ''
        } catch (e) { /* ignore */ }
        // 替换所有占位符：{船舶名}（可能出现多次）、{桥梁名}、{区域名}
        this.warningForm.description = template.text
          .replace(/\{船舶名\}/g, shipName)
          .replace(/\{桥梁名\}/g, bridgeName)
          .replace(/\{区域名\}/g, '')
          .replace(/\s{2,}/g, ' ')   // 清理区域名为空时多余的空格
          .trim()
        this.warningForm.alertType = this.selectedTemplate
      }
    },
    
    // 获取模板按钮的样式类
    getTemplateButtonClass(templateValue) {
      const classMap = {
        'YAW': 'yaw-template',
        'SPEED': 'speed-template', 
        'HEIGH': 'height-template',
        'STAY': 'stay-template',
        'ENTRY': 'entry-template',
        'EXIT': 'exit-template',
        'BLACKLIST': 'blacklist-template'
      }
      return classMap[templateValue] || ''
    },
    
    // 获取模板按钮的图标
    getTemplateIcon(templateValue) {
      const iconMap = {
        'YAW': 'el-icon-position',
        'SPEED': 'el-icon-lightning',
        'HEIGH': 'el-icon-top',
        'STAY': 'el-icon-time',
        'ENTRY': 'el-icon-right',
        'EXIT': 'el-icon-back',
        'BLACKLIST': 'el-icon-warning-outline'
      }
      return iconMap[templateValue] || 'el-icon-warning'
    },
    
    // 获取选中模板的标签
    getSelectedTemplateLabel() {
      const template = this.alertTemplates.find(t => t.value === this.selectedTemplate)
      return template ? template.label : ''
    },
    
    // 获取告警等级类型
    getAlertLevelType(level) {
      const typeMap = {
        1: 'danger',
        2: 'warning', 
        3: 'info'
      }
      return typeMap[level] || 'warning'
    },
    
    // 获取告警等级文本
    getAlertLevelText(level) {
      const textMap = {
        1: '警告',
        2: '警告',
        3: '警告'
      }
      return textMap[level] || '警告'
    },
    // 发送告警
    sendWarning() {
      // this.sendWarningLocal()
      this.sendWarningWithApi()
    },
    
    // 调用API发送告警
    sendWarningWithApi() {
      if (!this.warningForm.description) {
        this.$message.warning('告警内容不能为空')
        return
      }
      
      // 构建告警对象
      const alertData = {
        vesselMmsi: this.ship.userid,
        alertType: this.warningForm.alertType,
        alertLevel: this.warningForm.alertLevel,
        description: this.warningForm.description,
        alertTime: getUTC8ISOString().replace('+08:00', ''),  // LocalDateTime 不支持时区偏移
        longitude: this.ship.pos ? this.ship.pos.longitude : undefined,
        latitude: this.ship.pos ? this.ship.pos.latitude : undefined,
        // 添加船舶行驶方向和相对位置参数（如果选择未知则不填充）
        shipDirection: this.warningForm.shipDirection !== null ? this.warningForm.shipDirection : undefined,
        distanceTrend: this.warningForm.distanceTrend !== null ? this.warningForm.distanceTrend : undefined
      }

      // 从localstorage获取当前桥梁ID
      const selectedBridge = JSON.parse(localStorage.getItem('selectedBridge'))
      const currentBridgeId = Number(selectedBridge.id)
      
      console.log(`开始发送手动告警 - 桥梁ID: ${currentBridgeId}, 船舶: ${this.ship.cnName || this.ship.shipname || this.ship.userid}`)

      // 使用API发送手动告警
      sendManualAlert(currentBridgeId, alertData).then(response => {
        if (response.code === 200) {
          this.$message.success('告警发送成功')
          // 处理完成关闭对话框
          this.warningDialogVisible = false
        } else {
          this.$message.error(`告警发送失败: ${response.message}`)
        }
      }).catch(error => {
        console.error('发送告警失败', error)
        this.$message.error('告警发送失败，请稍后重试')
      })
    },

    // 发送告警
    sendWarningLocal() {
      if (!this.warningForm.description) {
        this.$message.warning('告警内容不能为空')
        return
      }
      
      // 构建告警对象
      const alertData = {
        alertId: 'manual_' + Date.now(),
        vesselMmsi: this.ship.userid,
        alertType: this.warningForm.alertType,
        alertLevel: this.warningForm.alertLevel,
        description: this.warningForm.description,
        alertTime: new Date().toISOString(),
        longitude: this.ship.pos ? this.ship.pos.longitude : undefined,
        latitude: this.ship.pos ? this.ship.pos.latitude : undefined
      }

      console.warn('【手动告警发送】：',alertData)
      alertDataReceiver.handleManualAlert(alertData)
      // 处理完成关闭对话框
      this.warningDialogVisible = false
    },
    
    // 重置告警表单
    resetWarningForm() {
      this.selectedTemplate = ''
      this.warningForm = {
        description: '',
        alertType: 'YAW',
        alertLevel: 2,
        shipDirection: 1, // 重置为默认值：上行
        distanceTrend: 0  // 重置为默认值：靠近
      }
      this.bridgeAlertDeviceStatus = null
    },
    
    // 转换为标准告警对象
    _convertToAlertObject(rawData) {
      const alertTypeMap = {
        'AREA_ENTRY': '区域进入',
        'AREA_EXIT': '区域离开',
        'SPEED_OVER': '超速',
        'HEIGHT_OVER': '超高',
        'COURSE_DEVIATION': '偏航',
        'STAY_OVERTIME': '滞留'
      }
      
      const alertLevelMap = {
        1: '警告',
        2: '警告',
        3: '警告'
      }
      
      return {
        id: rawData.alertId,
        mmsi: rawData.vesselMmsi,
        type: rawData.alertType,
        typeText: alertTypeMap[rawData.alertType] || rawData.alertType,
        level: rawData.alertLevel,
        levelText: alertLevelMap[rawData.alertLevel] || `${rawData.alertLevel}级警告`,
        description: rawData.description,
        time: this._formatTime(rawData.alertTime),
        originalTime: rawData.alertTime,
        position: {
          longitude: rawData.longitude,
          latitude: rawData.latitude
        }
      }
    },
    
    // 格式化时间
    _formatTime(timeStr) {
      try {
        const date = new Date(timeStr)
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        })
      } catch (e) {
        return timeStr || '未知时间'
      }
    }
  }
}
</script>

<style scoped>
/* 船舶信息面板样式 */
.ship-info-panel {
  position: absolute;
  top: 60px;
  left: 20px;
  width: 420px;
  z-index: 1000;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.ship-card {
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  background-color: rgba(255, 255, 255, 0.95);
}

.ship-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ship-card-actions {
  display: flex;
  align-items: center;
}

.ship-card-actions i {
  margin-left: 10px;
  font-size: 16px;
  cursor: pointer;
  color: #606266;
}

.ship-card-actions i:hover {
  color: #409EFF;
}

.ship-info-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 5px;
}

.ship-details-grid {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -5px;
}

.ship-details-col {
  flex: 1;
  min-width: 160px;
  padding: 0 5px;
}

.ship-detail-item {
  padding: 5px 0;
  border-bottom: 1px dashed #ebeef5;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
}

.detail-label {
  color: #606266;
  margin-right: 10px;
  min-width: 60px;
}

.detail-value {
  color: #303133;
  font-weight: 500;
}

.ship-detail-remark {
  margin-top: 10px;
  border-top: 1px solid #ebeef5;
  padding-top: 8px;
}

.full-width {
  grid-column: span 2;
}

.panel-footer {
  margin-top: 15px;
  display: flex;
  justify-content: center;
  gap: 10px;
}

/* 滑动动画 */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

/* 告警对话框样式 */
.warning-dialog-content {
  @apply space-y-6;
}

.section-title {
  @apply flex items-center text-base font-semibold text-gray-700 mb-3;
  margin: 0 0 12px 0;
}

.template-section {
  @apply bg-gray-50 p-4 rounded-lg;
}

.template-buttons {
  @apply grid grid-cols-2 gap-2;
}

.template-btn {
  @apply flex items-center justify-center px-3 py-2 transition-all duration-200;
  min-height: 36px;
  border-radius: 6px;
}

.template-btn:hover {
  @apply transform scale-105 shadow-md;
}

.template-btn.selected {
  @apply shadow-lg ring-2 ring-blue-300;
}

/* 不同类型告警按钮的颜色主题 */
.yaw-template.selected {
  @apply bg-orange-500 text-white border-orange-500;
}

.speed-template.selected {
  @apply bg-red-500 text-white border-red-500;
}

.height-template.selected {
  @apply bg-purple-500 text-white border-purple-500;
}

.stay-template.selected {
  @apply bg-yellow-500 text-white border-yellow-500;
}

.entry-template.selected {
  @apply bg-green-500 text-white border-green-500;
}

.exit-template.selected {
  @apply bg-blue-500 text-white border-blue-500;
}

.blacklist-template.selected {
  @apply bg-gray-800 text-white border-gray-800;
}

.ship-params-section {
  @apply bg-indigo-50 p-4 rounded-lg border border-indigo-200;
}

.params-grid {
  @apply grid grid-cols-1 gap-4;
}

.two-column-grid {
  @apply grid-cols-1 md:grid-cols-2 gap-6;
}

.param-item {
  @apply space-y-2;
}

.param-label {
  @apply flex items-center text-sm font-medium text-gray-700;
}

.param-select {
  @apply w-full;
}

.param-radio-group {
  @apply w-full flex flex-wrap gap-2;
}

.param-radio {
  @apply mr-2 mb-2;
}

.param-radio .el-radio__label {
  @apply text-sm text-gray-700;
}

.param-radio.is-checked .el-radio__label {
  @apply text-blue-600 font-medium;
}

/* 保留复选框样式以防其他地方使用 */
.param-checkbox-group {
  @apply w-full flex flex-wrap gap-2;
}

.param-checkbox {
  @apply mr-2 mb-2;
}

.param-checkbox .el-checkbox__label {
  @apply text-sm text-gray-700;
}

.param-checkbox.is-checked .el-checkbox__label {
  @apply text-blue-600 font-medium;
}

.content-section {
  @apply bg-blue-50 p-4 rounded-lg;
}

.content-textarea {
  @apply w-full;
}

.preview-section {
  @apply bg-green-50 p-4 rounded-lg border border-green-200;
}

.preview-card {
  @apply bg-white p-3 rounded-md shadow-sm space-y-2;
}

.preview-item {
  @apply flex items-center justify-between text-sm;
}

.preview-label {
  @apply text-gray-600 font-medium;
}

.preview-value {
  @apply text-gray-800 font-semibold;
}

.dialog-footer {
  @apply flex justify-end space-x-3 pt-4 border-t border-gray-200;
}

/* TailwindCSS 工具类 */
.mr-1 { margin-right: 0.25rem; }
.mr-2 { margin-right: 0.5rem; }
.text-orange-500 { color: #f97316; }
.text-blue-500 { color: #3b82f6; }
.text-green-500 { color: #10b981; }
.text-indigo-500 { color: #6366f1; }
.bg-gray-50 { background-color: #f9fafb; }
.bg-blue-50 { background-color: #eff6ff; }
.bg-green-50 { background-color: #f0fdf4; }
.bg-indigo-50 { background-color: #eef2ff; }
.p-4 { padding: 1rem; }
.p-3 { padding: 0.75rem; }
.rounded-lg { border-radius: 0.5rem; }
.rounded-md { border-radius: 0.375rem; }
.space-y-6 > * + * { margin-top: 1.5rem; }
.space-y-3 > * + * { margin-top: 0.75rem; }
.space-y-2 > * + * { margin-top: 0.5rem; }
.grid { display: grid; }
.grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.gap-2 { gap: 0.5rem; }
.flex { display: flex; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.justify-between { justify-content: space-between; }
.justify-end { justify-content: flex-end; }
.text-base { font-size: 1rem; }
.text-sm { font-size: 0.875rem; }
.font-semibold { font-weight: 600; }
.font-medium { font-weight: 500; }
.text-gray-700 { color: #374151; }
.text-gray-600 { color: #4b5563; }
.text-gray-800 { color: #1f2937; }
.shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
.shadow-md { box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
.shadow-lg { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); }
.transition-all { transition-property: all; }
.duration-200 { transition-duration: 200ms; }
.transform { transform: translateX(0) translateY(0) rotate(0) skewX(0) skewY(0) scaleX(1) scaleY(1); }
.scale-105 { transform: scale(1.05); }
.ring-2 { box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5); }
.ring-blue-300 { --tw-ring-color: #93c5fd; }
.border { border-width: 1px; }
.border-t { border-top-width: 1px; }
.border-gray-200 { border-color: #e5e7eb; }
.border-green-200 { border-color: #bbf7d0; }
.border-indigo-200 { border-color: #c7d2fe; }
.w-full { width: 100%; }
.pt-4 { padding-top: 1rem; }
.mb-3 { margin-bottom: 0.75rem; }
.bg-orange-500 { background-color: #f97316; }
.bg-red-500 { background-color: #ef4444; }
.bg-purple-500 { background-color: #a855f7; }
.bg-yellow-500 { background-color: #eab308; }
.bg-green-500 { background-color: #22c55e; }
.bg-blue-500 { background-color: #3b82f6; }
.bg-gray-800 { background-color: #1f2937; }
.bg-white { background-color: #ffffff; }
.text-white { color: #ffffff; }
.border-orange-500 { border-color: #f97316; }
.border-red-500 { border-color: #ef4444; }
.border-purple-500 { border-color: #a855f7; }
.border-yellow-500 { border-color: #eab308; }
.border-green-500 { border-color: #22c55e; }
.border-blue-500 { border-color: #3b82f6; }
.border-gray-800 { border-color: #1f2937; }
</style> 